import { useEffect, useRef, useState } from 'react';

/**
 * Fires `onEnter` once, when the returned sentinel gets close to the viewport.
 *
 * This is what splits the report into two requests: chapter 01 loads with the page, and chapter 02 is
 * fetched as the reader scrolls towards it. The 600px root margin means the request usually resolves
 * before the chapter is actually on screen, so the reveal reads as instant rather than as a load.
 *
 * The sentinel is a *callback* ref held in state, not a plain ref, because it sits inside the chapter-01
 * block and so does not exist on first paint. A plain ref would leave this effect with nothing to observe
 * at the only moments it ran, and no scroll would ever trigger anything. Keeping the node in state
 * re-runs the effect the moment it mounts — and again if it unmounts and comes back on a period change.
 *
 * `resetKey` re-arms the observer for a new reporting period or company.
 */
const useLazySection = (onEnter, resetKey) => {
  const handler = useRef(onEnter);
  handler.current = onEnter;
  const [node, setNode] = useState(null);

  useEffect(() => {
    // Sentinel not mounted yet — the callback ref re-runs this once it is.
    if (!node) return undefined;

    // No observer: fetch straight away rather than never.
    if (typeof IntersectionObserver === 'undefined') {
      handler.current();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        handler.current();
      },
      { rootMargin: '600px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [node, resetKey]);

  // `setNode` is a stable state setter, so using it as a ref does not detach and re-attach on every render.
  return setNode;
};

export default useLazySection;
